import React, { useCallback, useContext, useEffect, useState } from 'react';
import { InstituteAutomationService, InstituteRow, LivePreview, LivePreviewComponent, RunDetail, RunComponent } from '../instituteAutomationService';

const OVR_INPUT = 'form-control form-control-sm font-11 py-0.5 px-2 border w-auto bg-warning bg-opacity-10 text-warning-emphasis border-warning border-opacity-50';

// Shares the override map + setter with every metric card without prop-threading.
interface OvCtx { ov: Record<string, string>; onOv: (key: string, value: string) => void }
const OverrideContext = React.createContext<OvCtx>({ ov: {}, onOv: () => { } });

/** Preview metric card with an optional "Override" input.
 *  When an override is typed it is captured in state and immediately shown as
 *  the card's value (keyed by `ovKey`, defaulting to the label). */
const Pvc: React.FC<{
    label: string; value: React.ReactNode; foot: React.ReactNode;
    override?: string; ovKey?: string; cardClass?: string; cardStyle?: React.CSSProperties;
    labelClass?: string; valueClass?: string; footClass?: string;
}> = ({ label, value, foot, override, ovKey, cardClass, cardStyle, labelClass, valueClass, footClass }) => {
    const { ov, onOv } = useContext(OverrideContext);
    const editable = override !== undefined;
    const key = ovKey ?? label;
    const typed = editable ? ov[key] : undefined;               // undefined until user edits
    const shownValue = typed != null && typed !== '' ? typed : value;
    const inputVal = typed != null ? typed : (override ?? '');
    const dirty = editable && typed != null && typed !== override;
    return (
        <div className="col-12 col-sm-6 col-md-3">
            <div className={cardClass ?? 'card border border-light shadow-sm rounded-3 p-3 bg-white'} style={cardStyle}>
                <div className={labelClass ?? 'text-secondary font-10 font-600 mb-1'}>{label}</div>
                <div className={valueClass ?? 'font-20 font-600 text-dark mb-1'}>{shownValue}</div>
                <div className={footClass ?? 'font-11 text-secondary mb-2'}>{foot}</div>
                {editable && (
                    <div className="border-top border-light pt-2 d-flex align-items-center gap-2">
                        <span className="font-10 text-warning font-600 text-nowrap">Override:</span>
                        <input className={OVR_INPUT} type="text" value={inputVal} onChange={(e) => onOv(key, e.target.value)} />
                        {dirty && <span className="font-10 text-warning-emphasis" title="Overridden">✎</span>}
                    </div>
                )}
            </div>
        </div>
    );
};

const SectionHead: React.FC<{ badge: React.ReactNode; badgeClass: string; badgeStyle?: React.CSSProperties; title: string; status: React.ReactNode }> = ({ badge, badgeClass, badgeStyle, title, status }) => (
    <div className="card-header bg-white border-bottom py-3 px-4 d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="font-14 font-600 text-dark d-flex align-items-center gap-2">
            <span className={badgeClass} style={badgeStyle}>{badge}</span>
            {title}
        </div>
        <div className="d-flex align-items-center gap-3">
            {status}
            <div className="form-check form-switch mb-0"><input className="form-check-input" type="checkbox" role="switch" defaultChecked /></div>
        </div>
    </div>
);

const includeBanner = (text: string) => <span className="badge bg-success-subtle text-success font-11 px-2 py-1 rounded">{text}</span>;

// Per-section header chrome (emoji + badge) keyed by seeded section code.
const SEC_CHROME: Record<string, { emoji: string; badgeClass: string; badgeStyle?: React.CSSProperties }> = {
    profile_data_coverage: { emoji: '📊', badgeClass: 'badge bg-primary bg-opacity-10 text-primary px-2 py-1 font-12' },
    academic_professional_insights: { emoji: '🎓', badgeClass: 'badge px-2 py-1 font-12', badgeStyle: { backgroundColor: '#ede9fe', color: '#7c3aed' } },
    outreach_activities: { emoji: '📞', badgeClass: 'badge bg-secondary bg-opacity-10 text-secondary px-2 py-1 font-12' },
    campaign_event_activities: { emoji: '📣', badgeClass: 'badge bg-info bg-opacity-10 text-info px-2 py-1 font-12' },
    platform_activity: { emoji: '💻', badgeClass: 'badge bg-success bg-opacity-10 text-success px-2 py-1 font-12' },
};
const SEC_DEFAULT = { emoji: '📄', badgeClass: 'badge bg-secondary bg-opacity-10 text-secondary px-2 py-1 font-12' };

/** Format a live component value for display. */
const fmt = (c: LivePreviewComponent | undefined, fallback: string): string => {
    if (!c) return fallback;
    if (c.value == null) return c.generated ? '0' : fallback;
    return c.valueType === 'percentage' ? `${c.value}%` : String(c.value);
};

/** Trim a stored decimal string like "17698.0000" → "17698", "1.5000" → "1.5". */
const fmtNum = (x: string | number | null): string => {
    if (x == null) return '';
    const n = Number(x);
    return Number.isFinite(n) ? String(n) : String(x);
};
const statusLabel = (s: string): string =>
    s === 'pending_review' ? 'Pending review' : s === 'approved' ? 'Approved' : s === 'sent' ? 'Sent' : s;

const cardFoot = (c: LivePreviewComponent): string => {
    if (c.meta?.comparison_previous_period != null) return `Prev period: ${c.meta.comparison_previous_period}`;
    if (c.unit) return c.unit;
    return c.generated ? 'Live value' : '⚠️ No data — will be skipped';
};

// Normalized display cell — one shape for both live-compute and saved-run views.
interface NormCell { id: number; code: string; name: string; display: string; foot: string; ovDefault: string; ok: boolean }
const liveCell = (c: LivePreviewComponent): NormCell => {
    const v = fmt(c, '—');
    return { id: c.id, code: c.code, name: c.name, display: v, foot: cardFoot(c), ovDefault: v, ok: c.generated };
};
const runCell = (c: RunComponent): NormCell => {
    const isNum = c.valueType === 'numeric' || c.valueType === 'percentage';
    const effRaw = isNum ? c.effective.numeric : c.effective.text;
    const display = effRaw == null ? '—' : isNum ? `${fmtNum(effRaw)}${c.valueType === 'percentage' ? '%' : ''}` : String(effRaw);
    const ovDefault = effRaw == null ? '' : isNum ? fmtNum(effRaw) : String(effRaw);
    const foot = c.override ? `Overridden${c.override.reason ? ` · ${c.override.reason}` : ''}` : c.isIncluded ? 'Saved value' : '⚠️ Excluded';
    return { id: c.componentId, code: c.code, name: c.name, display, foot, ovDefault, ok: c.isIncluded };
};

const PreviewSend: React.FC<{ toast: (msg: string) => void; onSchedule: () => void; institute: InstituteRow | null }> = ({ toast, onSchedule, institute }) => {
    const [live, setLive] = useState<LivePreview | null>(null);
    const [runData, setRunData] = useState<RunDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(false);
    const [saving, setSaving] = useState(false);
    // SM value overrides typed in this session (keyed by component id).
    const [ov, setOv] = useState<Record<string, string>>({});
    const setOverride = useCallback((key: string, value: string) => setOv((prev) => ({ ...prev, [key]: value })), []);
    const overrideCount = Object.values(ov).filter((v) => v !== '').length;

    // Load the institute's report: prefer a saved run (shows effective = override ?? generated,
    // the exact values that will be mailed); fall back to a fresh live compute if none exists.
    const load = useCallback(async (inst: InstituteRow, resetOv: boolean) => {
        setLoading(true); setErr(false);
        if (resetOv) setOv({});
        try {
            const lp = await InstituteAutomationService.livePreview(inst.id);
            setLive(lp);
            const cur = await InstituteAutomationService.getCurrentRun(inst.id, lp.definition.id);
            if (cur?.id) setRunData(await InstituteAutomationService.getRun(cur.id));
            else setRunData(null);
        } catch (_) {
            setErr(true); setRunData(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!institute) { setLive(null); setRunData(null); setOv({}); return; }
        load(institute, true); // new institute → clear typed overrides
    }, [institute, load]);

    const instituteName = institute?.name ?? '—';

    // The effective view: saved run if one exists, else the live compute. Empty sections hidden.
    const viewSections = (runData
        ? runData.sections.map((s) => ({ id: s.id, code: s.code, name: s.name, cells: s.components.map(runCell) }))
        : live
            ? live.sections.map((s) => ({ id: s.id, code: s.code, name: s.name, cells: s.components.map(liveCell) }))
            : []
    ).filter((s) => s.cells.length > 0);
    // Editing is allowed on a live preview or a pending_review draft — not once approved/sent.
    const editable = !runData || runData.run.status === 'pending_review';

    const renderSection = (sec: { id: number; code: string; name: string; cells: NormCell[] }, idx: number) => {
        const chrome = SEC_CHROME[sec.code] ?? SEC_DEFAULT;
        const okCount = sec.cells.filter((c) => c.ok).length;
        const word = runData ? 'included' : 'generated live';
        return (
            <div className="card border border-light shadow-sm rounded-3 mb-4 overflow-hidden bg-white" key={sec.id}>
                <SectionHead
                    badge={chrome.emoji} badgeClass={chrome.badgeClass} badgeStyle={chrome.badgeStyle}
                    title={`Section ${idx + 1}: ${sec.name}`}
                    status={includeBanner(`✅ ${okCount}/${sec.cells.length} components ${word}`)}
                />
                <div className="card-body p-4 bg-light bg-opacity-25">
                    {sec.cells.length === 0 ? (
                        <div className="text-center text-secondary py-3 font-12">No components configured for this section yet.</div>
                    ) : (
                        <div className="row g-3">
                            {sec.cells.map((cell) => (
                                <Pvc key={cell.code} ovKey={String(cell.id)} label={cell.name} value={cell.display} foot={cell.foot} override={editable ? cell.ovDefault : undefined} />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Persist: reuse the pending draft if present, else generate a run; write the typed
    // overrides onto it; optionally approve. Then reload so the view shows the saved values.
    const persist = async (approve: boolean): Promise<number | null> => {
        if (!institute || !live) { toast('Select an institute first'); return null; }
        if (saving || !editable) return null;
        setSaving(true);
        try {
            let runId: number;
            if (runData && runData.run.status === 'pending_review') runId = runData.run.id;
            else runId = (await InstituteAutomationService.generateRun(institute.id, live.definition.id)).runId;

            // Map component id → value_type from whichever view is loaded.
            const typeById = new Map<number, string>();
            (runData ? runData.sections.flatMap((s) => s.components).map((c) => [c.componentId, c.valueType] as const)
                : live.sections.flatMap((s) => s.components).map((c) => [c.id, c.valueType] as const)
            ).forEach(([id, t]) => typeById.set(id, t));

            let applied = 0;
            for (const [idStr, raw] of Object.entries(ov)) {
                if (raw.trim() === '') continue;
                const id = Number(idStr);
                const vt = typeById.get(id);
                if (!vt) continue;
                const isNumeric = vt === 'numeric' || vt === 'percentage';
                try {
                    if (isNumeric) {
                        const n = Number(String(raw).replace(/[^0-9.\-]/g, ''));
                        if (!isFinite(n)) continue;
                        await InstituteAutomationService.overrideRunComponent(runId, id, { numeric: n, reason: 'SM preview override' });
                    } else {
                        await InstituteAutomationService.overrideRunComponent(runId, id, { text: String(raw), reason: 'SM preview override' });
                    }
                    applied++;
                } catch (_) { /* skip a single failed override, keep the rest */ }
            }

            if (approve) await InstituteAutomationService.approveRun(runId);
            await load(institute, true); // reflect saved effective values; clear typed overrides
            toast(approve
                ? `Approved & scheduled ✔ (run #${runId}, ${applied} override${applied === 1 ? '' : 's'})`
                : `Draft saved ✔ (run #${runId}, ${applied} override${applied === 1 ? '' : 's'})`);
            return runId;
        } catch (_) {
            toast(approve ? 'Could not approve — please try again' : 'Could not save draft — please try again');
            return null;
        } finally {
            setSaving(false);
        }
    };

    // ── Send Now (email delivery) ──
    const [sendOpen, setSendOpen] = useState(false);
    const [sendRunId, setSendRunId] = useState<number | null>(null);
    const [recips, setRecips] = useState<{ email: string; ssoUrl: string; checked: boolean }[]>([]);
    const [extraEmails, setExtraEmails] = useState('');
    const [loadingRecips, setLoadingRecips] = useState(false);
    const [sending, setSending] = useState(false);

    const openSendDialog = async () => {
        if (!institute || !live || saving) { if (!institute) toast('Select an institute first'); return; }
        // Send requires an approved run — reuse it if present, else generate + approve now.
        const runId = (runData && (runData.run.status === 'approved' || runData.run.status === 'sent'))
            ? runData.run.id
            : await persist(true);
        if (runId == null) return;
        setSendRunId(runId);
        setExtraEmails('');
        setSendOpen(true);
        setLoadingRecips(true);
        try {
            const rs = await InstituteAutomationService.getRunRecipients(runId);
            setRecips(rs.map((r) => ({ email: r.email, ssoUrl: r.ssoUrl, checked: true })));
        } catch (_) {
            setRecips([]); toast('Could not load recipients');
        } finally {
            setLoadingRecips(false);
        }
    };

    const doSend = async () => {
        if (sendRunId == null || sending) return;
        const extra = extraEmails.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
        const emails = [...recips.filter((r) => r.checked).map((r) => r.email), ...extra];
        if (!emails.length) { toast('Select or enter at least one email'); return; }
        setSending(true);
        try {
            const res = await InstituteAutomationService.sendRun(sendRunId, emails);
            toast(`Sent to ${res.sent}/${res.total} recipient${res.total === 1 ? '' : 's'} ✔`);
            setSendOpen(false);
            if (institute) await load(institute, true);
        } catch (e: unknown) {
            const err = e as { status?: number; response?: { status?: number }; message?: string };
            const status = err?.status ?? err?.response?.status;
            toast(status === 503 ? 'SendGrid isn’t configured yet (SENDGRID_API_KEY missing)' : (err?.message || 'Could not send'));
        } finally {
            setSending(false);
        }
    };
    const selectedCount = recips.filter((r) => r.checked).length;

    // ── Schedule (pick a future date) ──
    const [schedOpen, setSchedOpen] = useState(false);
    const [schedDate, setSchedDate] = useState('');
    const [schedFreq, setSchedFreq] = useState(1);
    const [scheduling, setScheduling] = useState(false);
    // Earliest selectable date = tomorrow (past + today disabled).
    const minDate = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();

    const doSchedule = async () => {
        if (!institute || scheduling) return;
        if (!schedDate || schedDate < minDate) { toast('Pick a future date'); return; }
        setScheduling(true);
        try {
            const r = await InstituteAutomationService.enableSchedule(institute.id, schedFreq, schedDate);
            const d = new Date(r.nextSendDate);
            toast(`Scheduled ${instituteName} for ${isNaN(d.getTime()) ? r.nextSendDate : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })} ✔`);
            setSchedOpen(false); setSchedDate('');
        } catch (_) {
            toast('Could not schedule — please try again');
        } finally {
            setScheduling(false);
        }
    };

    return (
        <OverrideContext.Provider value={{ ov, onOv: setOverride }}>
            <div className="alert alert-secondary border-0 text-white rounded-3 p-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-3 shadow" style={{ background: 'linear-gradient(135deg, #1c2b3a 0%, #243447 100%)' }}>
                <div className="d-flex align-items-center gap-3">
                    <span className="font-24">👁️</span>
                    <div>
                        <h6 className="mb-0 font-14 font-600 text-white">Report Preview — {instituteName}</h6>
                        <span className="font-11 text-white-50 mt-1 d-block">
                            {!institute
                                ? 'Select an institute from the Overview tab to preview its live report'
                                : loading ? 'Loading…' : err ? 'Data unavailable'
                                    : runData
                                        ? `Saved draft #${runData.run.id} · ${statusLabel(runData.run.status)} · these are the values that will be sent`
                                        : `Live data · Institute ID ${institute?.id} · ${live?.definition?.name ?? 'Report'}`}
                        </span>
                    </div>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button className="btn btn-sm btn-outline-light font-12 py-1 px-3" disabled={!institute || saving || loading} onClick={() => institute && load(institute, false)}>🔄 Refresh Data</button>
                    <button className="btn btn-sm text-white font-12 py-1 px-3 border-0" style={{ backgroundColor: '#f97316', opacity: institute && !saving && editable ? 1 : 0.6 }} disabled={!institute || saving || !editable} onClick={() => persist(true)}>✅ Approve &amp; Schedule</button>
                </div>
            </div>

            {/* Report sections — saved run if one exists, else live compute */}
            {!institute ? (
                <div className="card border border-light shadow-sm rounded-3 p-5 text-center bg-white mb-4">
                    <div className="font-36 mb-2">👈</div>
                    <h6 className="font-14 font-600 text-dark mb-1">No institute selected</h6>
                    <p className="font-12 text-secondary mb-0">Pick an institute from the Overview tab and click “Preview” to see its report.</p>
                </div>
            ) : loading ? (
                <div className="card border border-light shadow-sm rounded-3 p-5 text-center bg-white mb-4 text-secondary">
                    <div><span className="spinner-border spinner-border-sm me-2" />Loading values…</div>
                </div>
            ) : err ? (
                <div className="card border border-danger-subtle shadow-sm rounded-3 p-5 text-center bg-white mb-4 text-secondary">
                    <div className="font-36 mb-2">⚠️</div>
                    <h6 className="font-14 font-600 text-dark mb-1">Data unavailable</h6>
                    <p className="font-12 text-secondary mb-0">Couldn’t load the report for this institute. Try Refresh Data.</p>
                </div>
            ) : viewSections.length === 0 ? (
                <div className="card border border-light shadow-sm rounded-3 p-5 text-center bg-white mb-4 text-secondary font-12">No sections configured for this report.</div>
            ) : (
                viewSections.map(renderSection)
            )}

            {/* Action Bar */}
            <div className="sticky-bottom bg-white border py-3 px-4 d-flex flex-wrap justify-content-between align-items-center gap-3 mt-4 mb-0 shadow-sm rounded" style={{ zIndex: 1020, marginLeft: '-1.5rem', marginRight: '-1.5rem' }}>
                <div className="font-12 text-secondary">
                    {!institute ? <>📋 Select an institute to preview</>
                        : runData ? <>📋 Saved draft #{runData.run.id} · {statusLabel(runData.run.status)} for {instituteName}</>
                            : <>📋 Live report for {instituteName}</>}
                    {overrideCount > 0 && <span className="text-warning-emphasis font-600 ms-2">· {overrideCount} unsaved edit{overrideCount === 1 ? '' : 's'}</span>}
                    {!editable && <span className="text-secondary ms-2">· read-only (approved)</span>}
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <button className="btn btn-sm btn-outline-secondary font-12 font-600 px-3 py-1.5" disabled={!institute || saving || !editable} onClick={() => persist(false)}>💾 {saving ? 'Saving…' : 'Save Draft'}</button>
                    <button className="btn btn-sm btn-success font-12 font-600 px-3 py-1.5" disabled={!institute || saving || !editable} onClick={() => persist(true)}>✅ Approve</button>
                    <button className="btn btn-sm btn-outline-primary font-12 font-600 px-3 py-1.5" disabled={!institute || saving} onClick={() => { setSchedDate(''); setSchedOpen(true); }}>📅 Schedule</button>
                    <button className="btn btn-sm text-white font-12 font-600 px-3 py-1.5" style={{ backgroundColor: '#f97316' }} disabled={!institute || saving} onClick={openSendDialog}>📧 Send Now</button>
                </div>
            </div>

            {/* Send dialog */}
            {sendOpen && (
                <>
                    <div className="modal-backdrop fade show" />
                    <div className="modal fade show d-block" tabIndex={-1} onClick={(e) => { if (e.target === e.currentTarget && !sending) setSendOpen(false); }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title font-16 font-600 text-dark-blue">📧 Send Report — {instituteName}</h5>
                                    <button type="button" className="btn-close" aria-label="Close" disabled={sending} onClick={() => setSendOpen(false)} />
                                </div>
                                <div className="modal-body pt-2">
                                    <div className="d-flex justify-content-between align-items-center mb-2 gap-2">
                                        <div className="font-12 text-secondary">Institute admins for this client. Uncheck anyone you don’t want to email:</div>
                                        {recips.length > 0 && (
                                            <button type="button" className="btn btn-sm btn-outline-secondary font-11 px-2 py-1 text-nowrap"
                                                onClick={() => { const all = recips.every((r) => r.checked); setRecips((prev) => prev.map((r) => ({ ...r, checked: !all }))); }}>
                                                {recips.every((r) => r.checked) ? 'Deselect all' : 'Select all'}
                                            </button>
                                        )}
                                    </div>
                                    {loadingRecips ? (
                                        <div className="text-secondary font-12 py-3"><span className="spinner-border spinner-border-sm me-2" />Loading recipients…</div>
                                    ) : recips.length === 0 ? (
                                        <div className="alert alert-warning font-12 py-2 mb-3">No institute admins found for this institute — add emails below to send.</div>
                                    ) : (
                                        <div className="border rounded-2 p-2 mb-3" style={{ maxHeight: 220, overflowY: 'auto' }}>
                                            {recips.map((r, i) => (
                                                <div className="form-check py-1" key={r.email}>
                                                    <input className="form-check-input" type="checkbox" id={`recip-${i}`} checked={r.checked}
                                                        onChange={() => setRecips((prev) => prev.map((x, idx) => idx === i ? { ...x, checked: !x.checked } : x))} />
                                                    <label className="form-check-label font-12 text-dark" htmlFor={`recip-${i}`}>{r.email}</label>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div className="font-12 text-secondary mb-1">Add more emails (comma, space or newline separated):</div>
                                    <textarea className="form-control form-control-sm font-12" rows={2} value={extraEmails} placeholder="extra1@school.org, extra2@school.org"
                                        onChange={(e) => setExtraEmails(e.target.value)} />
                                    <div className="font-11 text-secondary mt-2">The “View Full Dashboard” button links each institute admin straight into their report via SSO.</div>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <span className="font-11 text-secondary me-auto">{selectedCount} selected{extraEmails.trim() ? ' + typed emails' : ''}</span>
                                    <button type="button" className="btn btn-sm btn-outline-secondary font-12 px-3" disabled={sending} onClick={() => setSendOpen(false)}>Cancel</button>
                                    <button type="button" className="btn btn-sm text-white font-12 px-3 border-0" style={{ backgroundColor: '#f97316' }} disabled={sending} onClick={doSend}>{sending ? 'Sending…' : '📧 Send Now'}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Schedule dialog — pick a FUTURE date (past + today disabled) */}
            {schedOpen && (
                <>
                    <div className="modal-backdrop fade show" />
                    <div className="modal fade show d-block" tabIndex={-1} onClick={(e) => { if (e.target === e.currentTarget && !scheduling) setSchedOpen(false); }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-3">
                                <div className="modal-header border-0 pb-1">
                                    <h5 className="modal-title font-16 font-600 text-dark-blue">📅 Schedule Report — {instituteName}</h5>
                                    <button type="button" className="btn-close" aria-label="Close" disabled={scheduling} onClick={() => setSchedOpen(false)} />
                                </div>
                                <div className="modal-body pt-2">
                                    <label className="text-uppercase text-secondary font-10 font-600 tracking-wide d-block mb-1">Send date (future only)</label>
                                    <input type="date" className="form-control form-control-sm font-13 mb-3" value={schedDate} min={minDate} onChange={(e) => setSchedDate(e.target.value)} />
                                    <label className="text-uppercase text-secondary font-10 font-600 tracking-wide d-block mb-1">Frequency</label>
                                    <select className="form-select form-select-sm font-13" value={schedFreq} onChange={(e) => setSchedFreq(Number(e.target.value))}>
                                        <option value={1}>Monthly</option>
                                        <option value={3}>Quarterly</option>
                                        <option value={6}>Semi-Annual</option>
                                        <option value={12}>Annual</option>
                                    </select>
                                    <div className="font-11 text-secondary mt-2">The report for <strong>{instituteName}</strong> will be scheduled for the chosen date and tracked in the log & Overview.</div>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <button type="button" className="btn btn-sm btn-outline-secondary font-12 px-3" disabled={scheduling} onClick={() => setSchedOpen(false)}>Cancel</button>
                                    <button type="button" className="btn btn-sm text-white font-12 px-3 border-0" style={{ backgroundColor: '#f97316', opacity: schedDate && !scheduling ? 1 : 0.6 }} disabled={!schedDate || scheduling} onClick={doSchedule}>{scheduling ? 'Scheduling…' : 'Confirm Schedule'}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </OverrideContext.Provider>
    );
};

export default PreviewSend;
